import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Search, Bell, Settings, Menu, PlayCircle } from "lucide-react";
import { usePlayer } from "../context/PlayerContext";
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger, DropdownMenuLabel, DropdownMenuSeparator } from "./ui/dropdown-menu";
import { Switch } from "./ui/switch";

export default function TopBar({ onOpenMobileNav }) {
  const navigate = useNavigate();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const [q, setQ] = useState(params.get("q") || "");
  const { autoplayNext, setAutoplayNext } = usePlayer();

  useEffect(() => { setQ(params.get("q") || ""); /* eslint-disable-next-line */ }, [location.search]);

  const submit = (e) => {
    e.preventDefault();
    const query = q.trim();
    navigate(query ? `/explore?q=${encodeURIComponent(query)}` : "/explore");
  };

  return (
    <header className="sticky top-0 z-30 border-b border-white/5 bg-[#0F0B08]/80 backdrop-blur-md">
      <div className="flex items-center gap-2 sm:gap-3 px-3 sm:px-5 lg:px-10 py-3 sm:py-4">
        <button
          onClick={onOpenMobileNav}
          className="lg:hidden p-2 rounded-md text-white/70 hover:text-paper hover:bg-white/5 shrink-0"
          aria-label="Open navigation"
          data-testid="topbar-menu-btn"
        >
          <Menu className="w-5 h-5" />
        </button>

        <form onSubmit={submit} className="flex-1 min-w-0 max-w-2xl" data-testid="search-form">
          <div className="flex items-center gap-2 sm:gap-3 h-11 sm:h-12 px-3 sm:px-5 rounded-full bg-white/[0.03] border border-white/5 focus-within:border-emerald-soft/60 transition-colors">
            <Search className="w-[18px] h-[18px] text-white/50 shrink-0" strokeWidth={1.5} />
            <input
              data-testid="search-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search the archives…"
              className="flex-1 min-w-0 bg-transparent outline-none font-display text-[15px] sm:text-[17px] italic text-white/85 placeholder:text-white/40"
            />
            {q && (
              <button
                type="button"
                onClick={() => { setQ(""); navigate("/explore"); }}
                className="text-xs label-caps text-white/50 hover:text-white/80"
              >
                Clear
              </button>
            )}
          </div>
        </form>

        <div className="ml-auto flex items-center gap-1 sm:gap-2 shrink-0">
          <button className="hidden sm:inline-flex p-2.5 rounded-full text-white/70 hover:text-gold hover:bg-white/[0.04] transition" aria-label="Notifications" data-testid="topbar-bell">
            <Bell className="w-[18px] h-[18px]" strokeWidth={1.5} />
          </button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                className="p-2.5 rounded-full text-white/70 hover:text-gold hover:bg-white/[0.04] transition data-[state=open]:text-gold"
                aria-label="Settings"
                data-testid="topbar-settings"
              >
                <Settings className="w-[18px] h-[18px]" strokeWidth={1.5} />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="bg-[#161009] border border-white/10 text-paper w-72 p-0">
              <DropdownMenuLabel className="px-4 pt-4 pb-2">
                <span className="label-caps text-gold/90">Settings</span>
              </DropdownMenuLabel>
              <DropdownMenuSeparator className="bg-white/5" />
              <div className="p-4 flex items-start gap-3" data-testid="setting-autoplay">
                <div className="mt-0.5">
                  <PlayCircle className="w-[18px] h-[18px] text-emerald-soft" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 justify-between">
                    <p className="text-[15px] font-display text-paper">Autoplay next lecture</p>
                    <Switch
                      checked={autoplayNext}
                      onCheckedChange={setAutoplayNext}
                      data-testid="setting-autoplay-switch"
                      aria-label="Autoplay next lecture"
                    />
                  </div>
                  <p className="text-xs text-white/55 leading-relaxed mt-1">
                    When a lecture ends, continue to the next one in the same series automatically.
                  </p>
                </div>
              </div>
            </DropdownMenuContent>
          </DropdownMenu>
          <div className="hidden sm:flex w-10 h-10 rounded-full bg-gradient-to-br from-gold/30 to-emerald-glow/20 border border-white/10 items-center justify-center font-display text-gold" data-testid="topbar-avatar">م</div>
        </div>
      </div>
    </header>
  );
}
